import {StyleType, wixFont, wixColor, CssColor, CssFont} from '@wix/editor-platform-ooi-metadata-resolver';
import {createStylesParams, StyleParamType, wixColorParam, wixFontParam} from '@wix/tpa-settings';

export enum StylesParamKeys {
  thankYou_textFontStyle = 'thankYou_textFontStyle',
  thankYou_titleFontStyle = 'thankYou_titleFontStyle',
  thankYou_borderColor = 'thankYou_borderColor',
  thankYou_dividersColor = 'thankYou_dividersColor',
  thankYou_textColor = 'thankYou_textColor',
  thankYou_titleColor = 'thankYou_titleColor',
  thankYou_background = 'thankYou_background',
  thankYou_widget_background = 'thankYou_widget_background',
  thankYou_borderWidth = 'thankYou_borderWidth',
  thankYou_linksTextFontStyle = 'thankYou_linksTextFontStyle',
  thankYou_linksTextColor = 'thankYou_linksTextColor',
  thankYou_infoTextFontStyle = 'thankYou_infoTextFontStyle',
  thankYou_infoTextColor = 'thankYou_infoTextColor',
  THANK_YOU_PAGE_ORDER_NUMBER_SELECTION = 'THANK_YOU_PAGE_ORDER_NUMBER_SELECTION',
  THANK_YOU_PAGE_IMAGE_SELECTION = 'THANK_YOU_PAGE_IMAGE_SELECTION',
  THANK_YOU_PAGE_CONTINUE_BROWSING_LINK_SELECTION = 'THANK_YOU_PAGE_CONTINUE_BROWSING_LINK_SELECTION',
}

export type IStylesParams = {
  [StylesParamKeys.thankYou_textFontStyle]: StyleParamType.Font;
  [StylesParamKeys.thankYou_titleFontStyle]: StyleParamType.Font;
  [StylesParamKeys.thankYou_borderColor]: StyleParamType.Color;
  [StylesParamKeys.thankYou_dividersColor]: StyleParamType.Color;
  [StylesParamKeys.thankYou_textColor]: StyleParamType.Color;
  [StylesParamKeys.thankYou_titleColor]: StyleParamType.Color;
  [StylesParamKeys.thankYou_background]: StyleParamType.Color;
  [StylesParamKeys.thankYou_widget_background]: StyleParamType.Color;
  [StylesParamKeys.thankYou_borderWidth]: StyleParamType.Number;
  [StylesParamKeys.thankYou_linksTextFontStyle]: StyleParamType.Font;
  [StylesParamKeys.thankYou_linksTextColor]: StyleParamType.Color;
  [StylesParamKeys.thankYou_infoTextFontStyle]: StyleParamType.Font;
  [StylesParamKeys.thankYou_infoTextColor]: StyleParamType.Color;
  [StylesParamKeys.THANK_YOU_PAGE_ORDER_NUMBER_SELECTION]: StyleParamType.Boolean;
  [StylesParamKeys.THANK_YOU_PAGE_IMAGE_SELECTION]: StyleParamType.Boolean;
  [StylesParamKeys.THANK_YOU_PAGE_CONTINUE_BROWSING_LINK_SELECTION]: StyleParamType.Boolean;
};

export default createStylesParams<IStylesParams>({
  [StylesParamKeys.thankYou_textFontStyle]: {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Body-M'),
    metadata: {
      type: StyleType.font,
      defaultValue: wixFont(CssFont.WstParagraph2Font),
      ai: {
        description: 'The font style for the thank you page text',
      },
    },
  },
  [StylesParamKeys.thankYou_titleFontStyle]: {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Body-M'),
    metadata: {
      type: StyleType.font,
      defaultValue: wixFont(CssFont.WstParagraph2Font),
      ai: {
        description: 'The font style for the thank you page title',
      },
    },
  },
  [StylesParamKeys.thankYou_borderColor]: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-2'),
    metadata: {
      type: StyleType.borderColor,
      defaultValue: wixColor(CssColor.WstSecondaryBackgroundColor),
      ai: {
        description: 'The border color for the thank you page widget',
      },
    },
  },
  [StylesParamKeys.thankYou_dividersColor]: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-2'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(CssColor.WstSecondaryBackgroundColor),
      ai: {
        description: 'The dividers color for the thank you page widget',
      },
    },
  },
  [StylesParamKeys.thankYou_textColor]: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(CssColor.WstParagraph2Color),
      ai: {
        description: 'The text color for the thank you page text',
      },
    },
  },
  [StylesParamKeys.thankYou_titleColor]: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(CssColor.WstParagraph2Color),
      ai: {
        description: 'The title color for the thank you page title',
      },
    },
  },
  [StylesParamKeys.thankYou_background]: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-1'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(CssColor.WstPrimaryBackgroundColor),
      ai: {
        description: 'The background color for the thank you page card',
      },
    },
  },
  [StylesParamKeys.thankYou_widget_background]: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-1'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(CssColor.WstPrimaryBackgroundColor),
      ai: {
        description: 'The background color for the thank you page body',
      },
    },
  },
  [StylesParamKeys.thankYou_borderWidth]: {
    type: StyleParamType.Number,
    getDefaultValue: /* istanbul ignore next */ () => 1,
    metadata: {
      type: StyleType.borderWidth,
      defaultValue: {
        type: 'static',
        value: 1,
      },
      ai: {
        description: 'The border width for the thank you page widget',
      },
    },
  },
  [StylesParamKeys.thankYou_linksTextFontStyle]: {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Body-M'),
    metadata: {
      type: StyleType.font,
      defaultValue: wixFont(CssFont.WstParagraph2Font),
      ai: {
        description: 'The font style for the thank you page links text',
      },
    },
  },
  [StylesParamKeys.thankYou_linksTextColor]: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(CssColor.WstParagraph2Color),
      ai: {
        description: 'The font style for the thank you page info text',
      },
    },
  },
  [StylesParamKeys.thankYou_infoTextFontStyle]: {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Body-M'),
    metadata: {
      type: StyleType.font,
      defaultValue: wixFont(CssFont.WstParagraph2Font),
      ai: {
        description: 'The font style for the thank you page info text',
      },
    },
  },
  [StylesParamKeys.thankYou_infoTextColor]: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(CssColor.WstParagraph2Color),
      ai: {
        description: 'The text color for the thank you page info text',
      },
    },
  },
  [StylesParamKeys.THANK_YOU_PAGE_ORDER_NUMBER_SELECTION]: {
    type: StyleParamType.Boolean,
    getDefaultValue: () => true,
    metadata: {
      type: StyleType.booleanValue,
      defaultValue: {
        type: 'static',
        value: true,
      },
      ai: {
        description: 'Should show the order number on the thank you page',
      },
    },
  },
  [StylesParamKeys.THANK_YOU_PAGE_IMAGE_SELECTION]: {
    type: StyleParamType.Boolean,
    getDefaultValue: () => true,
    metadata: {
      type: StyleType.booleanValue,
      defaultValue: {
        type: 'static',
        value: true,
      },
      ai: {
        description: 'Should show line item images on the thank you page',
      },
    },
  },
  [StylesParamKeys.THANK_YOU_PAGE_CONTINUE_BROWSING_LINK_SELECTION]: {
    type: StyleParamType.Boolean,
    getDefaultValue: () => true,
    metadata: {
      type: StyleType.booleanValue,
      defaultValue: {
        type: 'static',
        value: true,
      },
      ai: {
        description: 'Should show continue browsing link on the thank you page',
      },
    },
  },
});
